/**
 * Alert Store
 * Zustand store for real-time trading alerts and market data
 */

import { create } from 'zustand';

// ============================================================================
// TYPES
// ============================================================================

export type Asset = 'BTC' | 'ETH' | 'SOL' | 'HYPE' | 'LIT';

export interface Alert {
  id: string;
  type: string;
  asset: Asset;
  timestamp: number;
  message?: string;
  direction?: 'LONG' | 'SHORT';
  confidence?: number;
  [key: string]: unknown;
}

export interface Trade {
  id: string;
  asset: Asset;
  exchange: string;
  side: 'buy' | 'sell';
  size: number;
  price: number;
  usd: number;
  timestamp: number;
}

export interface PriceData {
  spot: Record<string, number>;
  perp: Record<string, number>;
}

export interface CVDData {
  spotTotal: { net: number };
  perpTotal: { net: number };
}

export interface SourceStatus {
  coinglass: boolean;
  binance_perp: boolean;
  binance_spot: boolean;
  bybit_perp: boolean;
  hyperliquid: boolean;
  coinbase: boolean;
  [key: string]: boolean;
}

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface AlertStore {
  // Connection state
  connected: boolean;
  connecting: boolean;
  error: string | null;
  sources: SourceStatus;

  // Data
  alerts: Alert[];
  trades: Trade[];
  prices: Record<Asset, PriceData>;
  cvd: Record<Asset, CVDData>;
  selectedAsset: Asset;

  // Actions
  setConnected: (connected: boolean) => void;
  setConnecting: (connecting: boolean) => void;
  setError: (error: string | null) => void;
  setSources: (sources: Partial<SourceStatus> | Record<string, boolean>) => void;
  addAlert: (alert: Alert) => void;
  addAlerts: (alerts: Alert[]) => void;
  addTrade: (trade: Trade) => void;
  updatePrice: (asset: Asset, exchange: string, marketType: 'spot' | 'perp', price: number) => void;
  updateCVD: (asset: Asset, data: Partial<CVDData>) => void;
  setSelectedAsset: (asset: Asset) => void;
  setInitialState: (state: Partial<InitialState>) => void;
  reset: () => void;
}

interface InitialState {
  alerts?: Alert[];
  largeTrades?: Trade[];
  prices?: Record<string, PriceData>;
  cvd?: Record<string, CVDData>;
  sources?: Partial<SourceStatus> | Record<string, boolean>;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const ASSETS: Asset[] = ['BTC', 'ETH', 'SOL', 'HYPE', 'LIT'];

const createEmptyPrices = (): Record<Asset, PriceData> => {
  const prices: Partial<Record<Asset, PriceData>> = {};
  ASSETS.forEach((asset) => {
    prices[asset] = { spot: {}, perp: {} };
  });
  return prices as Record<Asset, PriceData>;
};

const createEmptyCVD = (): Record<Asset, CVDData> => {
  const cvd: Partial<Record<Asset, CVDData>> = {};
  ASSETS.forEach((asset) => {
    cvd[asset] = { spotTotal: { net: 0 }, perpTotal: { net: 0 } };
  });
  return cvd as Record<Asset, CVDData>;
};

const initialSources: SourceStatus = {
  coinglass: false,
  binance_perp: false,
  binance_spot: false,
  binance_depth: false,
  bybit_perp: false,
  bybit_spot: false,
  hyperliquid: false,
  coinbase: false,
  kraken: false,
  aggr: false,
  lighter: false,
};

const initialState = {
  connected: false,
  connecting: false,
  error: null,
  sources: initialSources,
  alerts: [] as Alert[],
  trades: [] as Trade[],
  prices: createEmptyPrices(),
  cvd: createEmptyCVD(),
  selectedAsset: 'BTC' as Asset,
};

// ============================================================================
// STORE
// ============================================================================

export const useAlertStore = create<AlertStore>((set) => ({
  ...initialState,

  setConnected: (connected) => set({ connected, connecting: false }),
  setConnecting: (connecting) => set({ connecting }),
  setError: (error) => set({ error }),
  setSources: (sources) => set((state) => ({
    sources: { ...state.sources, ...sources } as SourceStatus
  })),

  addAlert: (alert) =>
    set((state) => ({
      alerts: [alert, ...state.alerts].slice(0, 200),
    })),

  addAlerts: (alerts) =>
    set((state) => ({
      alerts: [...alerts, ...state.alerts].slice(0, 200),
    })),

  addTrade: (trade) =>
    set((state) => ({
      trades: [trade, ...state.trades].slice(0, 100),
    })),

  updatePrice: (asset, exchange, marketType, price) =>
    set((state) => {
      const currentAssetPrices = state.prices[asset] || { spot: {}, perp: {} };
      return {
        prices: {
          ...state.prices,
          [asset]: {
            ...currentAssetPrices,
            [marketType]: {
              ...currentAssetPrices[marketType],
              [exchange]: price,
            },
          },
        },
      };
    }),

  updateCVD: (asset, data) =>
    set((state) => {
      const currentCVD = state.cvd[asset] || { spotTotal: { net: 0 }, perpTotal: { net: 0 } };
      return {
        cvd: {
          ...state.cvd,
          [asset]: {
            ...currentCVD,
            ...data,
          },
        },
      };
    }),

  setSelectedAsset: (asset) => set({ selectedAsset: asset }),

  setInitialState: (data) =>
    set((state) => ({
      alerts: data.alerts || state.alerts,
      trades: data.largeTrades || state.trades,
      prices: (data.prices as Record<Asset, PriceData>) || state.prices,
      cvd: (data.cvd as Record<Asset, CVDData>) || state.cvd,
      sources: data.sources
        ? ({ ...state.sources, ...data.sources } as SourceStatus)
        : state.sources,
    })),

  reset: () => set(initialState),
}));

// ============================================================================
// SELECTORS
// ============================================================================

/**
 * Get flat price for an asset (best available)
 */
export function getFlatPrice(prices: PriceData): number {
  return (
    prices.perp.binance ||
    prices.spot.binance ||
    prices.perp.hyperliquid ||
    Object.values(prices.perp)[0] ||
    Object.values(prices.spot)[0] ||
    0
  );
}

/**
 * Get net CVD for an asset
 */
export function getNetCVD(cvd: CVDData): number {
  return (cvd.spotTotal?.net || 0) + (cvd.perpTotal?.net || 0);
}

/**
 * Get connected source count
 */
export function getConnectedSourceCount(sources: SourceStatus): number {
  return Object.values(sources).filter(Boolean).length;
}
